'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useMemo, useState } from 'react';
import { ExternLogoFull } from '@extern/ui/logos';
import Link from 'next/link';
import { LabsSurfaceProvider } from '@/app/(platform)/(subscription-gated)/labs/_engine/labs-surface';
import { PackProvider } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import { getPreviewVisitorId } from '../_engine/anonymous-id';
import { isEmbedded, readPlacementFromLocation } from '../_engine/entry-placement';
import { readGuideSourceFromLocation } from '../_engine/guide-source';
import { PREVIEW_ENTRY, readOpeningAnswerFromLocation } from '../_engine/opening-answer';
import { PreviewAnalyticsProvider } from '../_engine/preview-analytics';
import { PreviewContainer } from '../_engine/preview-container';
import { toPreviewPack } from '../_engine/preview-copy';
import { PreviewFlow } from '../_engine/preview-flow';
import { resolvePreviewPack } from '../_engine/preview-packs';
import { PREVIEW_MODULE_COUNT, PREVIEW_STEPS, PREVIEW_STORE_VARIANT } from '../_engine/preview-steps';

/**
 * The public preview's client tree, and the one place its four providers are composed in order:
 *
 *   PackProvider              the vertical's content, plus a store bound to the SHORT step sequence
 *     LabsSurfaceProvider     two modules, no trial bar (the sprint's screens ask the host for both)
 *       PreviewContainer      seeds the `?a1=` opening answer, restores the draft, never gates render
 *         PreviewAnalyticsProvider   arm-free tracking, which needs the visitor id and entry path
 *           PreviewFlow
 *
 * The analytics provider sits INSIDE the container on purpose. `labs_preview_viewed` is the
 * denominator for the whole funnel, and firing it before the visitor id resolved would leave the
 * fastest-bouncing readers carrying a null id. Those are the readers the number is most about.
 */

/** Height of the header, matching `theme(spacing.16)`, which is what `StepShell` subtracts from the viewport. */
const HEADER_CLASS = 'flex h-16 shrink-0 items-center justify-between border-fs-border border-b bg-fs-surface px-4';

/**
 * Takes the vertical SLUG rather than a pack, because a pack cannot cross the server-to-client
 * boundary: it carries `deriveArchetype` and a Lucide icon, and React serializes neither. The
 * registry is a plain module, so resolving here reaches exactly the same pack.
 */
export function PreviewApp(t0) {
  const $ = _c(26);
  const {
    vertical
  } = t0;
  const [visitorId] = useState(getPreviewVisitorId);
  const [guideSource] = useState(readGuideSourceFromLocation);
  const [openingAnswer] = useState(readOpeningAnswerFromLocation);
  const [placement] = useState(readPlacementFromLocation);
  let t1;
  if ($[0] !== placement) {
    t1 = isEmbedded(placement);
    $[0] = placement;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const embedded = t1;
  let t2;
  if ($[2] !== vertical) {
    t2 = resolvePreviewPack(vertical);
    $[2] = vertical;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const pack = t2;
  let t3;
  if ($[4] !== pack) {
    t3 = pack ? toPreviewPack(pack) : null;
    $[4] = pack;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const previewPack = t3;
  if (!previewPack) {
    return null;
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      steps: PREVIEW_STEPS,
      variant: PREVIEW_STORE_VARIANT
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      moduleCount: PREVIEW_MODULE_COUNT,
      trialBar: false
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== embedded) {
    t6 = embedded ? null : <header className={HEADER_CLASS}><Link aria-label="Extern" href="https://extern.com"><ExternLogoFull className="h-6 w-auto" /></Link><Link className="rounded-fs-pill px-4 py-2 font-medium text-fs-body-md text-fs-brand hover:bg-fs-brand-subtle" href="/sign-in">Sign in</Link></header>;
    $[8] = embedded;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = openingAnswer ? PREVIEW_ENTRY.QUESTION : PREVIEW_ENTRY.LINK;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <PreviewFlow />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== guideSource || $[12] !== placement || $[13] !== t7 || $[14] !== visitorId) {
    t9 = <PreviewAnalyticsProvider entry={t7} guideSource={guideSource} placement={placement} visitorId={visitorId}>{t8}</PreviewAnalyticsProvider>;
    $[11] = guideSource;
    $[12] = placement;
    $[13] = t7;
    $[14] = visitorId;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== embedded || $[17] !== openingAnswer || $[18] !== t9) {
    t10 = <PreviewContainer openingAnswer={openingAnswer} startAtDeck={embedded}>{t9}</PreviewContainer>;
    $[16] = embedded;
    $[17] = openingAnswer;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t6) {
    t11 = <LabsSurfaceProvider surface={t5}><div className="flex min-h-dvh flex-col bg-fs-canvas">{t6}{t10}</div></LabsSurfaceProvider>;
    $[20] = t10;
    $[21] = t6;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== previewPack || $[24] !== t11) {
    t12 = <PackProvider pack={previewPack} storeOptions={t4}>{t11}</PackProvider>;
    $[23] = previewPack;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}